import { createReducer, on } from '@ngrx/store';

import { ResellersActions, RouterActions, AppActions } from '@actions/index'
import { ResellerModel } from '@models/index'
import { emptyResellerModel } from '@utilities/data-utils';

/**
 * Admin's app State. Defines the
 * app State that will store resellers
 * information.
 */
export interface State {
    /**
    * resellers: An array of {@link ResellerModel} 
    * that contains all app resellers
    * data.
    */
    resellers: ResellerModel[];
    /**
    * resellers: An array of {@link ResellerModel} 
    * that contains all string matching resellers
    * data.
    */
    foundResellersByQuery: ResellerModel[];
    /**
     * searchResellerQuery: A variable that
     * contains the text to search matching 
     * resellers.
     */
    searchResellerQuery: string;
    /**
     * selectedReseller: Stores the current
     * selected reseller on the app.
     */
    selectedReseller: ResellerModel;
    /**
     * resellerById: Stores the reseller that
     * matched the resellerId search.
     */
    resellerById: ResellerModel;
    /**
     * error: Stores any catched petition error.
     */
    error: string;
    /**
     * isExecutingTask: a flag that specifies 
     * whether a task is currently running or not
     */
    isExecutingTask: boolean;
    resellerCount: number;

}

/**
 * initialState stores the initial values
 * for Admin's app state
 */
const initialState: State = {
    resellers: [],
    foundResellersByQuery: [],
    searchResellerQuery: '',
    selectedReseller: emptyResellerModel(),
    resellerById: emptyResellerModel(),
    error: '',
    isExecutingTask: false,
    resellerCount: 0
};

/**
 * reducer creates the admin's reducer.
 * This reducer listens to all succesfull
 * related admin actions to store the returned 
 * data to admin's app state depending
 * on the action
 */
export const reducer = createReducer(
    initialState,
    on(ResellersActions.searchResellers, (state) => ({ ...state, resellers: [], isExecutingTask: true })),
    on(ResellersActions.searchResellersSuccess, (state, { data, total }) => ({ ...state, resellers: data, resellerCount: total, isExecutingTask: false })),
    on(ResellersActions.createResellerSuccess, (state, { reseller }) => ({ ...state, resellers: [...state.resellers, reseller], selectedReseller: reseller })),
    on(ResellersActions.resllersError, (state, { error }) => ({ ...state, error: error })),
    on(ResellersActions.searchResllersByQuerySuccess, (state, { foundResellers }) => ({ ...state, foundResellersByQuery: foundResellers })),
    on(ResellersActions.editResellerSuccess, (state, { reseller }) => ({ ...state, resllers: state.resellers.map((resellerItem => reseller.id === resellerItem.id ? reseller : resellerItem)), selectedReseller: reseller })),
    on(ResellersActions.deleteResellerSuccess, (state, { resellerId }) => ({ ...state, resllers: state.resellers.filter(reseller => reseller.id !== resellerId) })),
    on(ResellersActions.archiveResellerSuccess, (state, { resellerId }) => ({ ...state, resllers: state.resellers.filter(reseller => reseller.id !== resellerId) })),
    on(ResellersActions.searchResllersByQuerySuccess, (state, { foundResellers }) => ({ ...state, resellers: foundResellers })),
    on(ResellersActions.selectResellerProfile, (state, { reseller }) => ({ ...state, selectedReseller: reseller })),
    on(ResellersActions.clearSelectedReseller, (state) => ({ ...state, selectedReseller: emptyResellerModel() })),
    on(ResellersActions.searchResellerByBusinessSuccess, (state, { reseller }) => ({ ...state, selectedReseller: reseller })),
    on(ResellersActions.getResellerById, (state, { }) => ({ ...state, resellerById: emptyResellerModel() })),
    on(ResellersActions.getResellerByIdSuccess, (state, { reseller }) => ({ ...state, resellerById: reseller })),
    on(RouterActions.navigateAuth, () => ({ ...initialState })),
    on(AppActions.clearState, (state) => {
        return ({
            ...state,
            resellers: [],
            foundResellersByQuery: [],
        })
    }),
);
/**
 * Returns the isExecutingTask property
 * from admin's app state
 * @param state 
 */
export const getIsTaskExecuting = (state: State) => state.isExecutingTask;
/**
 * Returns the error property
 * from admin's app state
 * @param state 
 */
export const getError = (state: State) => state.error;
/**
 * Returns the foundResellersByQuery property
 * from admin's app state
 * @param state 
 */
export const getResellersByQuery = (state: State) => state.foundResellersByQuery;
/**
 * Returns the selectedReseller property
 * from admin's app state
 * @param state 
 */
export const getSelectedReseller = (state: State) => state.selectedReseller;
/**
 * Returns the resellerById property
 * from admin's app state
 * @param state 
 */
export const getResellerById = (state: State) => state.resellerById;
/**
 * Returns the searchResellerQuery property
 * from admin's app state
 * @param state 
 */
export const getSearchResellerQuery = (state: State) => state.searchResellerQuery;
/**
 * Returns the resellers property
 * from admin's app state
 * @param state 
 */
export const getResellers = (state: State) => state.resellers;
export const getResellerCount = (state: State) => state.resellerCount;